import React, { useState } from 'react';
import { Loader2, MoreVertical } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { WeekPoint } from './api';

export const SOURCE_LABEL: Record<string, string> = {
  curated_model: 'Curated',
  seller_model: 'Seller',
  barcode_model: 'Barcode',
};

export const SourceBadge: React.FC<{ source: string | null; isPrivate?: boolean }> = ({
  source,
  isPrivate,
}) => (
  <span className="inline-flex items-center gap-1">
    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
      {SOURCE_LABEL[source || ''] || source || 'Unknown'}
    </span>
    {isPrivate ? (
      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">Private</span>
    ) : null}
  </span>
);

export const fmtDay = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** "item_model_name" reads as "Item model name" when a type has no friendly name. */
export const attrLabel = (name?: string | null) =>
  !name ? '' : name.includes('_') ? name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : name;

export interface MenuEntry {
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  separatorBefore?: boolean;
  hidden?: boolean;
}

/** Row actions: one dotted menu, an icon per entry. */
export const RowMenu: React.FC<{ entries: MenuEntry[]; label?: string }> = ({ entries, label }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        aria-label={label || 'Actions'}
        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      {entries
        .filter((e) => !e.hidden)
        .map((e) => (
          <React.Fragment key={e.label}>
            {e.separatorBefore && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={e.onSelect}
              className={`gap-2 ${e.danger ? 'text-red-600 focus:text-red-700' : ''}`}
            >
              {e.icon}
              {e.label}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export interface ConfirmRequest {
  title: string;
  body: React.ReactNode;
  confirm: string;
  danger?: boolean;
  run: () => Promise<unknown> | unknown;
}

/** One confirmation dialog a page drives with `ask(...)`. */
export const useConfirm = () => {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const dialog = (
    <AlertDialog open={!!req} onOpenChange={(open) => !open && !busy && setReq(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{req?.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-gray-600">{req?.body}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            className={req?.danger ? 'bg-red-600 hover:bg-red-700' : undefined}
            onClick={async (e) => {
              e.preventDefault();
              if (!req) return;
              setBusy(true);
              try {
                await req.run();
              } finally {
                setBusy(false);
                setReq(null);
              }
            }}
          >
            {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {req?.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { ask: setReq, dialog };
};

/** A weekly trend with its total, for the header cards. */
export const TrendCard: React.FC<{
  label: string;
  points?: WeekPoint[];
  hint: string;
  color: string;
  loading?: boolean;
}> = ({ label, points, hint, color, loading }) => {
  const total = (points || []).reduce((s, p) => s + p.count, 0);
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="text-sm font-semibold text-gray-900">
          {loading ? '…' : total.toLocaleString()}
        </p>
      </div>
      <div className="mt-2 h-12">
        {loading ? (
          <div className="flex h-full items-center gap-2 text-xs text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading trend
          </div>
        ) : points && points.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <Tooltip
                formatter={(v: number) => [v.toLocaleString(), 'Count']}
                labelFormatter={(_, p) => (p?.[0]?.payload?.week ? `Week of ${p[0].payload.week}` : '')}
              />
              <Area type="monotone" dataKey="count" stroke={color} fill={color} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="pt-3 text-xs text-gray-400">Nothing in the last 12 weeks.</p>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-400">{hint}</p>
    </div>
  );
};
